import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { MAX_ESIGNATURE_BULK_CREATE } from 'config/Vars'
import { AccessForSelectFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { AccessSelector } from 'modules/core/components/AccessSelector'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import { useCreateEsignatureRequests } from 'modules/esignatures/hooks/requests/useCreateEsignatureRequests'

interface Props {
  document: Document
  onClose: () => void
}

const EsignatureModal = ({ document, onClose }: Props) => {
  const employee = document.employee
  const { create } = useCreateEsignatureRequests()
  const { access: currentAccess } = useCurrentSession()

  const [selected, setSelected] = React.useState<AccessForSelectFragment[]>(
    employee ? [employee.access] : []
  )

  const flashMaxError = () => {
    app.setFlash({
      type: 'wrong',
      message: i18n.t('documents.modal.esignature.error_max_selected_users', {
        count: MAX_ESIGNATURE_BULK_CREATE,
      }),
    })
  }

  const onSelect = (accesses: AccessForSelectFragment[]) => {
    if (accesses.length >= MAX_ESIGNATURE_BULK_CREATE) return flashMaxError()

    setSelected(accesses)
  }

  const { running, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      app.setProgress({
        message: i18n.t('common.loading'),
      })

      await create({
        documentId: document.id,
        requesterId: currentAccess.id,
        signeesId: selected.map((access) => access.id),
      })

      app.setProgress(null)

      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      closePortal()
    },
    {
      successFlash: false,
    }
  )

  return (
    <Modal
      illustration="esignature"
      title={i18n.t('documents.modal.esignature.modal.title')}
      description={i18n.t('documents.modal.esignature.modal.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button submit disabled={running || !selected.length}>
          {i18n.tp('documents.modal.esignature.modal.submit', {
            count: selected.length,
          })}
        </Button>
      }
    >
      <AccessSelector multiple value={selected} onSelect={onSelect} />
    </Modal>
  )
}

export default observer(EsignatureModal)
